import { useProductivity } from '../../contexts/ProductivityContext';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { AlertTriangle, Clock, MousePointerClick } from 'lucide-react';

export default function IdleModal() {
  const { isIdle, idleTime, handleStopIdle, formatIdleTime, totalIdleEvents } = useProductivity();
  const location = useLocation();

  // Don't show idle modal on public portal routes, candidate portal, or login pages
  const excludedRoutes = [
    '/', '/onboarding', '/submit-cv', '/hr-review', '/interview-schedule', 
    '/interview-select', '/hr-swipe', '/hr-optout', '/hr-reopen',
    '/my', '/ats/login', '/auth', '/reset-password', '/ats/reset-password'
  ];
  const isExcludedRoute = excludedRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Also check if user is logged in as a recruiter (not candidate)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isRecruiter = user?.role && ['recruiter', 'admin', 'super_admin'].includes(user.role);
  
  // Only show idle modal for logged-in recruiters on ATS pages (not login, not candidate portal)
  const isATSPage = location.pathname.startsWith('/ats') && !location.pathname.includes('/login');
  
  if (!isIdle || isExcludedRoute || !isRecruiter || !isATSPage) return null;

  // Format detailed time
  const hours = Math.floor(idleTime / 3600);
  const minutes = Math.floor((idleTime % 3600) / 60);
  const seconds = idleTime % 60;

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center"
      data-testid="idle-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in zoom-in-95 duration-200">
        {/* Warning Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
            <AlertTriangle className="w-10 h-10 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">
          You Are Idle!
        </h2>
        <p className="text-center text-slate-500 mb-6">
          No keyboard or mouse activity detected
        </p>

        {/* Timer Display */}
        <div className="bg-slate-100 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Time Idle</span>
          </div>
          
          <div className="flex justify-center gap-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600">{hours.toString().padStart(2, '0')}</div>
              <div className="text-xs text-slate-500 uppercase">Hours</div>
            </div>
            <div className="text-4xl font-bold text-slate-300">:</div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600">{minutes.toString().padStart(2, '0')}</div>
              <div className="text-xs text-slate-500 uppercase">Minutes</div>
            </div>
            <div className="text-4xl font-bold text-slate-300">:</div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600">{seconds.toString().padStart(2, '0')}</div>
              <div className="text-xs text-slate-500 uppercase">Seconds</div>
            </div>
          </div>
        </div>

        {/* Idle Count Badge */}
        <div className="flex justify-center mb-6">
          <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
            Idle event #{totalIdleEvents} today
          </span>
        </div>

        {/* Stop Button */}
        <Button 
          onClick={handleStopIdle}
          size="lg"
          className="w-full h-14 text-lg font-semibold bg-green-600 hover:bg-green-700 text-white"
          data-testid="stop-idle-btn"
        >
          <MousePointerClick className="w-5 h-5 mr-2" />
          STOP - I'm Back!
        </Button>

        <p className="text-center text-xs text-slate-400 mt-4">
          This idle time will be logged and reported
        </p>
      </div>
    </div>
  );
}
